import { supabase } from '@/lib/supabaseClient';

// Keywords for intent detection
const propertyKeywords = ['property', 'properties', 'apartment', 'home', 'house', 'units', 'real estate'];
const blogKeywords = ['blog', 'article', 'news', 'story', 'read'];
const bookingKeywords = ['book', 'booking', 'visit', 'tour', 'viewing', 'schedule', 'appointment'];
const testimonialKeywords = ['testimonial', 'review', 'feedback', 'experience'];
const contactKeywords = ['contact', 'call', 'email', 'phone', 'reach'];
const helpKeywords = ['help', 'assist', 'support', 'guide'];

// Conversation states (reserved for future flows)
const CONVERSATION_STATES = {
  INITIAL: 'initial',
  COLLECTING_INFO: 'collecting_info',
  BOOKING_APPOINTMENT: 'booking_appointment',
  FORM_SUBMISSION: 'form_submission',
  CONFIRMATION: 'confirmation',
};

// Form types
const FORM_TYPES = {
  CONTACT: 'contact',
  INQUIRY: 'inquiry',
  FEEDBACK: 'feedback',
};

const containsKeyword = (text, keywords) =>
  keywords.some((keyword) => text.toLowerCase().includes(keyword));

// Enhanced intent detection
const detectIntent = (message) => {
  const text = message.toLowerCase();

  if (containsKeyword(text, bookingKeywords) && containsKeyword(text, ['schedule', 'book', 'appointment'])) {
    return 'book_appointment';
  }
  if (containsKeyword(text, bookingKeywords)) {
    return 'booking_info';
  }
  if (containsKeyword(text, propertyKeywords)) {
    return 'property_inquiry';
  }
  if (containsKeyword(text, blogKeywords)) {
    return 'blog_inquiry';
  }
  if (containsKeyword(text, testimonialKeywords)) {
    return 'testimonial_inquiry';
  }
  if (containsKeyword(text, contactKeywords)) {
    return 'contact_request';
  }
  if (containsKeyword(text, helpKeywords) || text.includes('?')) {
    return 'help_request';
  }
  if (text.includes('yes') || text.includes('sure') || text.includes('okay')) {
    return 'affirmative';
  }
  if (text.includes('no') || text.includes('cancel') || text.includes('stop')) {
    return 'negative';
  }

  return 'general';
};

// User profile management
const getOrCreateUserProfile = async (conversationId) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('conversation_id', conversationId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.warn('Error fetching user profile:', error);
      return null;
    }

    return data || null;
  } catch (err) {
    console.warn('Error in getOrCreateUserProfile:', err);
    return null;
  }
};

const updateUserProfile = async (conversationId, profileData) => {
  try {
    const existing = await getOrCreateUserProfile(conversationId);

    if (existing) {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ ...profileData, updated_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({ conversation_id: conversationId, ...profileData })
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  } catch (err) {
    console.warn('Error updating user profile:', err);
    return null;
  }
};

// Appointment booking - saves to existing 'bookings' table
const createAppointment = async (appointmentData) => {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .insert({
        full_name: appointmentData.name || 'Chatbot User',
        email: appointmentData.email || null,
        phone: appointmentData.phone || null,
        preferred_date: appointmentData.date || null,
        preferred_time: appointmentData.time || null,
        notes: appointmentData.notes || '',
        status: 'new',
        source: 'chatbot',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    console.warn('Error creating appointment:', err);
    return null;
  }
};

// Form submission
const submitForm = async (conversationId, formType, formData) => {
  try {
    const { data, error } = await supabase
      .from('form_submissions')
      .insert({
        conversation_id: conversationId,
        form_type: formType,
        form_data: formData,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    console.warn('Error submitting form:', err);
    return null;
  }
};

// Extract information from user message
const extractContactInfo = (message) => {
  const info = {};

  // Extract phone number (simple pattern)
  const phoneMatch = message.match(/(\+?[\d\s\-\(\)]{10,})/);
  if (phoneMatch) {
    info.phone = phoneMatch[1].trim();
  }

  // Extract email
  const emailMatch = message.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
  if (emailMatch) {
    info.email = emailMatch[1];
  }

  // Extract name (look for common name patterns)
  const namePatterns = [
    /(?:my name is|i am|this is|call me)\s+([A-Za-z\s]+)/i,
    /(?:hi|hello),?\s+(?:my name is\s+)?([A-Za-z\s]+)/i,
  ];

  for (const pattern of namePatterns) {
    const match = message.match(pattern);
    if (match) {
      info.name = match[1].trim();
      break;
    }
  }

  return info;
};

// Parse date and time from message
const parseDateTime = (message) => {
  const text = message.toLowerCase();
  const now = new Date();

  if (text.includes('tomorrow')) {
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    return { date: tomorrow.toISOString().split('T')[0] };
  }

  if (text.includes('today')) {
    return { date: now.toISOString().split('T')[0] };
  }

  const timeMatch = message.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (timeMatch) {
    let hour = parseInt(timeMatch[1], 10);
    const minute = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
    const ampm = timeMatch[3]?.toLowerCase();

    if (ampm === 'pm' && hour < 12) hour += 12;
    if (ampm === 'am' && hour === 12) hour = 0;

    return { time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}` };
  }

  return {};
};

const formatPrice = (value) => {
  if (value === null || value === undefined) {
    return 'pricing upon request';
  }
  const cleanedValue = String(value).replace(/[^\d.-]/g, '');
  const num = Number(cleanedValue);
  if (Number.isNaN(num)) {
    return `${value}`;
  }
  return `UGX ${num.toLocaleString('en-UG')}`;
};

const summarizeList = (items, formatter) =>
  items
    .map(formatter)
    .filter(Boolean)
    .join('\n- ');

const fetchPropertiesSnapshot = async () => {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select('id,title,location,price,status')
      .order('created_at', { ascending: false })
      .limit(3);
    if (error) {
      console.warn('Failed to fetch properties from Supabase:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.warn('Error fetching properties:', err.message);
    return [];
  }
};

const fetchBlogSnapshot = async () => {
  try {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('id,title,excerpt,date')
      .eq('published', true)
      .order('date', { ascending: false })
      .limit(3);
    if (error) {
      console.warn('Failed to fetch blog posts from Supabase:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.warn('Error fetching blog posts:', err.message);
    return [];
  }
};

const fetchTestimonialsSnapshot = async () => {
  try {
    const { data, error } = await supabase
      .from('testimonials')
      .select('id,name,location,message')
      .order('created_at', { ascending: false })
      .limit(2);
    if (error) {
      console.warn('Failed to fetch testimonials from Supabase:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.warn('Error fetching testimonials:', err.message);
    return [];
  }
};

const fetchStats = async () => {
  try {
    const [propStats, bookingStats] = await Promise.all([
      supabase.from('properties').select('id', { count: 'exact', head: true }),
      supabase.from('bookings').select('id', { count: 'exact', head: true }),
    ]);

    return {
      properties: propStats.count ?? 0,
      bookings: bookingStats.count ?? 0,
    };
  } catch (err) {
    console.warn('Error fetching stats:', err.message);
    return {
      properties: 0,
      bookings: 0,
    };
  }
};

const buildPropertiesAnswer = async () => {
  const [properties, testimonials] = await Promise.all([
    fetchPropertiesSnapshot(),
    fetchTestimonialsSnapshot(),
  ]);

  if (!properties.length) {
    return "I'm currently updating our property listings. You can still reach us at bookings@nyinimu.com for the latest available properties.";
  }

  const propertyLines = summarizeList(properties, (property) => {
    const status = property.status || 'available';
    const location = property.location || 'location on request';
    return `${property.title} in ${location} — ${formatPrice(property.price)} (${status})`;
  });

  let testimonialsText = '';
  if (testimonials.length) {
    testimonialsText = `\n\nBuyers say:\n- ${summarizeList(
      testimonials,
      (t) => {
        const message = (t.message || '').slice(0, 80).trim();
        const suffix = message.length === 80 ? '...' : '';
        return `${t.name || 'Client'} (${t.location || 'Kampala'}) said: "${message}${suffix}"`;
      }
    )}`;
  }

  return `Here are a few properties available right now:\n- ${propertyLines}\n\nNeed full brochures or payment plans? Reply with "schedule a visit" and I'll guide you.${testimonialsText}`;
};

const buildBlogAnswer = async () => {
  const blogs = await fetchBlogSnapshot();
  if (!blogs.length) {
    return 'I do not have blog posts in Supabase yet, but the editorial team is preparing new stories.';
  }

  const blogLines = summarizeList(blogs, (blog) => {
    const date = blog.date ? new Date(blog.date).toLocaleDateString('en-UG', { month: 'short', day: 'numeric' }) : '';
    return `${blog.title}${date ? ` (${date})` : ''}`;
  });

  return `Fresh from the Nyinimu blog:\n- ${blogLines}\n\nVisit nyinimu.com/blog for the full articles or tell me which topic you want summarized.`;
};

const buildBookingAnswer = async () =>
  'To book a viewing, share your name, phone number, and the property you like. I will log it and a Nyinimu concierge will call you. You can also use the Schedule Visit form on nyinimu.com.';

const buildTestimonialAnswer = async () => {
  const testimonials = await fetchTestimonialsSnapshot();
  if (!testimonials.length) {
    return 'We are collecting testimonials from recent buyers. Check back soon!';
  }

  const testimonialLines = summarizeList(testimonials, (t) =>
    `${t.name || 'Client'} (${t.location || 'Kampala'}) said: "${(t.message || '').slice(0, 120)}..."`
  );

  return `Here is what Nyinimu buyers are saying:\n- ${testimonialLines}\n\nWant to add your story? Let me know and I will collect the details.`;
};

const buildGeneralAnswer = async () => {
  const stats = await fetchStats();
  return `Hi! I'm the Nyinimu AI Assistant. I can help you with:

- Browse Properties: See our latest apartments, houses, and land
- Book Appointments: Schedule property viewings and consultations
- Contact Us: Get connected with our team
- Read Articles: Browse our blog posts and real estate insights

We currently have ${stats.properties} properties available and ${stats.bookings} active bookings. What would you like to know about?`;
};

const buildAssistantReply = async (message, conversationId) => {
  const intent = detectIntent(message);
  const userProfile = await getOrCreateUserProfile(conversationId);

  const extractedInfo = extractContactInfo(message);
  if (Object.keys(extractedInfo).length > 0 && userProfile) {
    await updateUserProfile(conversationId, extractedInfo);
  }

  // Re-fetch after updating with extracted info
  const updatedProfile = await getOrCreateUserProfile(conversationId);

  switch (intent) {
    case 'book_appointment':
      return await handleAppointmentBooking(message, conversationId, updatedProfile);
    case 'booking_info':
      return await buildBookingAnswer();
    case 'property_inquiry':
      return await buildPropertiesAnswer();
    case 'blog_inquiry':
      return await buildBlogAnswer();
    case 'testimonial_inquiry':
      return await buildTestimonialAnswer();
    case 'contact_request':
      return await handleContactRequest(message, conversationId, updatedProfile);
    case 'help_request':
      return buildHelpAnswer();
    case 'affirmative':
      return await handleAffirmativeResponse(conversationId, updatedProfile);
    case 'negative':
      return 'No problem! Let me know if you need anything else.';
    default:
      return await buildGeneralAnswer();
  }
};

const handleAppointmentBooking = async (message, conversationId, userProfile) => {
  const extractedInfo = extractContactInfo(message);
  const dateTimeInfo = parseDateTime(message);

  if (Object.keys(extractedInfo).length > 0) {
    await updateUserProfile(conversationId, extractedInfo);
  }

  const profile = await getOrCreateUserProfile(conversationId);
  const hasBasicInfo = profile?.name && (profile?.phone || profile?.email);

  if (!hasBasicInfo) {
    return "I'd be happy to schedule an appointment for you! First, could you please share your name and contact information (phone or email)?";
  }

  // Auto-book with minimal info if user confirmed they want to book
  if (!dateTimeInfo.date || !dateTimeInfo.time) {
    return 'Great! I can schedule an appointment for you. What date and time would work best? For example: "tomorrow at 2pm" or "next Monday at 10am".';
  }

  // Create appointment with date/time
  const appointmentData = {
    name: profile.name || extractedInfo.name,
    email: profile.email || extractedInfo.email,
    phone: profile.phone || extractedInfo.phone,
    date: dateTimeInfo.date,
    time: dateTimeInfo.time,
    notes: `Requested via chatbot. Message: ${message}`,
  };

  const appointment = await createAppointment(appointmentData);

  if (appointment) {
    return `Perfect! I've booked your appointment for ${dateTimeInfo.date} at ${dateTimeInfo.time}. A Nyinimu representative will contact you soon at ${profile.phone || profile.email} to confirm. Your booking reference is #${String(appointment.id).slice(-8).toUpperCase()}.`;
  } else {
    return 'I apologize, but there was an issue scheduling your appointment. Please try again or contact us directly at +256-200-920200 or +256-762-534774.';
  }
};

const handleContactRequest = async (message, conversationId, userProfile) => {
  const extractedInfo = extractContactInfo(message);

  if (Object.keys(extractedInfo).length > 0) {
    await updateUserProfile(conversationId, extractedInfo);
  }

  const profile = await getOrCreateUserProfile(conversationId);
  const hasBasicInfo = profile?.name && (profile?.phone || profile?.email);

  // If user has given contact info and requested to book/visit, auto-create booking
  if (hasBasicInfo && (message.toLowerCase().includes('book') || message.toLowerCase().includes('visit') || message.toLowerCase().includes('appointment'))) {
    const appointment = await createAppointment({
      name: profile.name,
      email: profile.email,
      phone: profile.phone,
      notes: `Booking request via chatbot: ${message}`,
    });

    if (appointment) {
      return `Excellent! I've logged your booking request. A Nyinimu representative will contact you shortly at ${profile.phone || profile.email} to confirm your appointment. Your booking reference is #${String(appointment.id).slice(-8).toUpperCase()}.`;
    }
  }

  // Otherwise, just submit contact form
  const formData = {
    message,
    timestamp: new Date().toISOString(),
    ...extractedInfo,
  };

  const submission = await submitForm(conversationId, FORM_TYPES.CONTACT, formData);

  if (submission) {
    return "Thank you for reaching out! I've recorded your contact request and a team member will get back to you within 24 hours. You can also call us directly at +256-200-920200 or +256-762-534774.";
  } else {
    return 'Thank you for your message! You can reach us directly at +256-200-920200 or +256-762-534774.';
  }
};

const handleAffirmativeResponse = async (conversationId, userProfile) => {
  return 'Great! How can I help you further? I can show you properties, schedule appointments, or answer any questions about Nyinimu.';
};

const buildHelpAnswer = () => {
  return `I'm here to help! I can assist you with:

- Properties: Browse available apartments, houses, and land
- Appointments: Schedule property viewings and consultations
- Contact: Get in touch with our team
- Blog: Read our latest articles and news
- Feedback: Share your experience or ask questions

Just tell me what you're interested in, or ask me a question!`;
};

const ensureConversation = async (conversationId) => {
  if (conversationId) return conversationId;
  try {
    const { data, error } = await supabase
      .from('ai_conversations')
      .insert({ topic: 'general' })
      .select('id')
      .single();

    if (error) {
      console.warn('Failed to create conversation in Supabase:', error.message);
      return `temp-${Date.now()}`;
    }
    return data.id;
  } catch (err) {
    console.warn('Error creating conversation:', err.message);
    return `temp-${Date.now()}`;
  }
};

const persistMessage = async (conversationId, role, content) => {
  try {
    if (!conversationId.startsWith('temp-')) {
      await supabase
        .from('ai_messages')
        .insert({ conversation_id: conversationId, role, content })
        .throwOnError();
    }
  } catch (err) {
    console.warn('Failed to persist message to Supabase:', err.message);
  }
};

export const sendChatMessage = async (text, existingConversationId) => {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    throw new Error('Please enter a message.');
  }

  const conversationId = await ensureConversation(existingConversationId);
  await persistMessage(conversationId, 'user', trimmed);

  const reply = await buildAssistantReply(trimmed, conversationId);
  await persistMessage(conversationId, 'assistant', reply);

  return {
    conversationId,
    reply,
  };
};
